import { useState } from 'react';
import { Loader2, Upload } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUploadPiece } from '../hooks/use-dossier-company';
import {
  DEFAULT_VALIDITY_MONTHS,
  PIECE_TYPES_ORDER,
  PIECE_TYPE_LABELS,
} from './piece-types';
import type { PieceType } from '../types/dossier-company.types';

const ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/png'];
const MAX_SIZE = 50 * 1024 * 1024;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PieceUploadDialog({ open, onOpenChange }: Props) {
  const upload = useUploadPiece();
  const [file, setFile] = useState<File | null>(null);
  const [type, setType] = useState<PieceType | ''>('');
  const [deliveredAt, setDeliveredAt] = useState('');
  const [validityMonths, setValidityMonths] = useState<string>('');
  const [issuingBody, setIssuingBody] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [fileError, setFileError] = useState<string | null>(null);

  function reset() {
    setFile(null);
    setType('');
    setDeliveredAt('');
    setValidityMonths('');
    setIssuingBody('');
    setReferenceNumber('');
    setNotes('');
    setFileError(null);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    if (!f) {
      setFile(null);
      setFileError(null);
      return;
    }
    if (!ALLOWED_MIME.includes(f.type)) {
      setFileError('Format non autorisé. PDF, JPEG, PNG uniquement.');
      setFile(null);
      return;
    }
    if (f.size > MAX_SIZE) {
      setFileError(`Taille > 50 Mo (fichier : ${(f.size / 1024 / 1024).toFixed(1)} Mo)`);
      setFile(null);
      return;
    }
    setFileError(null);
    setFile(f);
  }

  function handleTypeChange(t: PieceType) {
    setType(t);
    if (!validityMonths) {
      const dft = DEFAULT_VALIDITY_MONTHS[t];
      if (dft !== undefined) setValidityMonths(String(dft));
    }
  }

  async function handleSubmit() {
    if (!file || !type) return;
    await upload.mutateAsync({
      file,
      type,
      filename: file.name,
      mimeType: file.type,
      sizeBytes: file.size,
      deliveredAt: deliveredAt || undefined,
      validityMonths: validityMonths ? parseInt(validityMonths, 10) : undefined,
      issuingBody: issuingBody || undefined,
      referenceNumber: referenceNumber || undefined,
      notes: notes || undefined,
    });
    reset();
    onOpenChange(false);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Ajouter une pièce administrative</DialogTitle>
          <DialogDescription>
            PDF, JPEG ou PNG · 50 Mo max. La pièce sera disponible pour toutes les opportunités.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="file">Fichier</Label>
            <Input
              id="file"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              onChange={handleFileChange}
            />
            {fileError && <p className="text-sm text-destructive">{fileError}</p>}
            {file && (
              <p className="text-sm text-muted-foreground">
                {file.name} · {(file.size / 1024).toFixed(0)} Ko
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="type">Type de pièce</Label>
            <Select value={type} onValueChange={(v) => handleTypeChange(v as PieceType)}>
              <SelectTrigger id="type">
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent>
                {PIECE_TYPES_ORDER.map((t) => (
                  <SelectItem key={t} value={t}>
                    {PIECE_TYPE_LABELS[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="delivered">Date de délivrance</Label>
              <Input
                id="delivered"
                type="date"
                value={deliveredAt}
                onChange={(e) => setDeliveredAt(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="validity">Validité (mois)</Label>
              <Input
                id="validity"
                type="number"
                min={0}
                value={validityMonths}
                onChange={(e) => setValidityMonths(e.target.value)}
                placeholder="ex. 36"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="issuing">Organisme émetteur</Label>
            <Input
              id="issuing"
              value={issuingBody}
              onChange={(e) => setIssuingBody(e.target.value)}
              placeholder="ex. AFNOR, Greffe, URSSAF…"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reference">Numéro de référence</Label>
            <Input
              id="reference"
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={!file || !type || upload.isPending}>
            {upload.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-2 h-4 w-4" />
            )}
            Uploader
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
